/**
 * 会员列表（platform.user.list）
 */
import { useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Tag, Descriptions, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { OperatorForm } from '../../components/OperatorForm';
import { ShiftUplineForm } from '../../components/ShiftUplineModal';
import { DeleteUserForm } from '../../components/DeleteUserModal';
import { useAuthStore } from '../../store/auth';
import { useTabsStore } from '../../store/tabs';
import { ADMIN_MENU_LEAVES } from '../../config/admin-menu-tree';
import { TOKEN_IDS, type TokenId } from '@uo/shared';
import { api, ApiError } from '../../api/client';

interface UserRow {
  id: number;
  username: string;
  phone: string | null;
  level_code: string;
  status: string;
  kyc_status: string;
  create_time: string;
}

interface BalancePreview {
  user_id: number;
  asset: string;
  balance_before: string;
  delta: string;
  balance_after: string;
  would_apply?: boolean;
}

const detailLeaf = ADMIN_MENU_LEAVES.find((l) => l.route === 'platform.user.detail');

export interface UsersListPageProps {
  pageTitle?: string;
  initialStatus?: string;
  initialKycStatus?: string;
  /** 展示 KYC 列，并对 pending 提供 通过/拒绝（PATCH kyc_status） */
  kycReviewMode?: boolean;
}

export function UsersListPage({
  pageTitle,
  initialStatus,
  initialKycStatus,
  kycReviewMode = false,
}: UsersListPageProps = {}) {
  const [username, setUsername] = useState('');
  const [status, setStatus] = useState<string | undefined>(initialStatus);
  const [kycFilter, setKycFilter] = useState<string | undefined>(initialKycStatus);
  const [adjustTarget, setAdjustTarget] = useState<UserRow | null>(null);
  const [shiftTarget, setShiftTarget] = useState<UserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [freezeTarget, setFreezeTarget] = useState<UserRow | null>(null);
  const [freezeForm] = Form.useForm();
  const hasNode = useAuthStore((s) => s.hasNode);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const openTab = useTabsStore((s) => s.open);

  const canAdjust = hasNode('platform.user.adjust');
  const canShiftUpline = hasNode('platform.user.shift_upline');
  const canDelete = hasNode('platform.user.delete');
  const canFreeze = hasNode('platform.user.freeze');
  const canView = hasNode('platform.user.view');
  const canEdit = hasNode('platform.user.edit');

  async function setKyc(row: UserRow, kyc_status: 'approved' | 'rejected') {
    try {
      await api.patch(`/api/admin/users/${row.id}`, { kyc_status });
      message.success('已更新 KYC 状态');
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '更新失败');
    }
  }

  function goUserDetail(row: UserRow) {
    if (!detailLeaf || !canView) return;
    openTab({
      key: `platform.user.detail:${row.id}`,
      label: `会员 #${row.id}`,
      path: `${detailLeaf.path}?id=${row.id}`,
      closable: true,
    });
    navigate(`${detailLeaf.path}?id=${row.id}`);
  }

  async function submitFreeze(values: { reason: string }) {
    if (!freezeTarget) return;
    try {
      await api.post(`/api/admin/users/${freezeTarget.id}/freeze`, { reason: values.reason });
      message.success('已冻结');
      setFreezeTarget(null);
      freezeForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '操作失败');
    }
  }

  function confirmUnfreeze(row: UserRow) {
    Modal.confirm({
      title: '确认解冻该用户？',
      onOk: async () => {
        try {
          await api.post(`/api/admin/users/${row.id}/unfreeze`);
          message.success('已解冻');
          qc.invalidateQueries({ queryKey: ['admin', 'users'] });
        } catch (e) {
          message.error(e instanceof ApiError ? e.message : '操作失败');
        }
      },
    });
  }

  const extraParams = useMemo(
    () => ({
      username: username || undefined,
      status,
      kyc_status: kycFilter,
    }),
    [username, status, kycFilter],
  );

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '用户名', dataIndex: 'username' },
    { title: '手机', dataIndex: 'phone', render: (v: string | null) => v ?? '—' },
    { title: '等级', dataIndex: 'level_code', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: string) => {
        const ok = v === 'active';
        return <Tag color={ok ? 'green' : 'red'}>{ok ? '正常' : v}</Tag>;
      },
    },
    {
      title: 'KYC',
      dataIndex: 'kyc_status',
      width: 100,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    { title: '注册时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      key: 'actions',
      width: 320,
      render: (_: unknown, row: UserRow) => (
        <Space wrap>
          {canView && (
            <Button size="small" type="link" onClick={() => goUserDetail(row)}>
              详情
            </Button>
          )}
          {kycReviewMode && canEdit && row.kyc_status === 'pending' && (
            <>
              <Button size="small" type="primary" onClick={() => setKyc(row, 'approved')}>
                KYC 通过
              </Button>
              <Button size="small" danger onClick={() => setKyc(row, 'rejected')}>
                拒绝
              </Button>
            </>
          )}
          {canAdjust && (
            <Button size="small" onClick={() => setAdjustTarget(row)}>
              调整余额
            </Button>
          )}
          {canShiftUpline && (
            <Button size="small" onClick={() => setShiftTarget(row)}>
              平移上级
            </Button>
          )}
          {canDelete && (
            <Button size="small" danger onClick={() => setDeleteTarget(row)}>
              删除
            </Button>
          )}
          {canFreeze && row.status === 'active' && (
            <Button size="small" danger onClick={() => setFreezeTarget(row)}>
              冻结
            </Button>
          )}
          {canFreeze && row.status === 'frozen' && (
            <Button size="small" onClick={() => confirmUnfreeze(row)}>
              解冻
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {pageTitle && <h2 style={{ marginTop: 0 }}>{pageTitle}</h2>}
      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search
          placeholder="搜索用户名"
          style={{ width: 220 }}
          allowClear
          onSearch={(v) => setUsername(v)}
        />
        <Select
          placeholder="状态"
          style={{ width: 140 }}
          allowClear
          value={status}
          onChange={(v) => setStatus(v)}
          options={[
            { value: 'active', label: '正常' },
            { value: 'frozen', label: '冻结' },
            { value: 'locked', label: '锁定' },
            { value: 'banned', label: '封禁' },
          ]}
        />
        <Select
          placeholder="KYC"
          style={{ width: 140 }}
          allowClear
          value={kycFilter}
          onChange={(v) => setKycFilter(v)}
          options={[
            { value: 'none', label: 'none' },
            { value: 'pending', label: 'pending' },
            { value: 'approved', label: 'approved' },
            { value: 'rejected', label: 'rejected' },
          ]}
        />
      </Space>

      <DataTable<UserRow>
        queryKey={['admin', 'users', username, status ?? '', kycFilter ?? '', pageTitle ?? '']}
        endpoint="/api/admin/users"
        extraParams={extraParams}
        columns={columns}
        rowKey="id"
      />

      <Modal
        open={adjustTarget !== null}
        title={`调整余额 — ${adjustTarget?.username ?? ''}`}
        footer={null}
        onCancel={() => setAdjustTarget(null)}
        destroyOnClose
      >
        {adjustTarget && (
          <OperatorForm<BalancePreview>
            endpoint={`/api/admin/users/${adjustTarget.id}/adjust-balance`}
            fields={[
              {
                name: 'asset',
                label: '资产',
                type: 'select',
                required: true,
                options: TOKEN_IDS.map((t: TokenId) => ({ value: t, label: t.toUpperCase() })),
              },
              {
                name: 'delta',
                label: '变动量（可正可负，如 100 或 -50）',
                type: 'text',
                required: true,
                placeholder: '100 或 -50',
              },
              { name: 'note', label: '备注', type: 'textarea', required: true },
            ]}
            renderPreview={(p) => (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="用户 ID">{p.user_id}</Descriptions.Item>
                <Descriptions.Item label="资产">{p.asset}</Descriptions.Item>
                <Descriptions.Item label="调整前">{p.balance_before}</Descriptions.Item>
                <Descriptions.Item label="变动">{p.delta}</Descriptions.Item>
                <Descriptions.Item label="调整后">
                  <strong>{p.balance_after}</strong>
                </Descriptions.Item>
              </Descriptions>
            )}
            onDone={() => {
              setAdjustTarget(null);
              qc.invalidateQueries({ queryKey: ['admin', 'users'] });
            }}
          />
        )}
      </Modal>

      <Modal
        open={shiftTarget !== null}
        title={`平移上级 — ${shiftTarget?.username ?? ''}`}
        footer={null}
        onCancel={() => setShiftTarget(null)}
        destroyOnClose
        width={560}
      >
        {shiftTarget && (
          <ShiftUplineForm
            userId={shiftTarget.id}
            username={shiftTarget.username}
            onDone={() => {
              setShiftTarget(null);
              qc.invalidateQueries({ queryKey: ['admin', 'users'] });
            }}
          />
        )}
      </Modal>

      <Modal
        open={deleteTarget !== null}
        title={`删除会员 — ${deleteTarget?.username ?? ''}`}
        footer={null}
        onCancel={() => setDeleteTarget(null)}
        destroyOnClose
        width={560}
      >
        {deleteTarget && (
          <DeleteUserForm
            userId={deleteTarget.id}
            username={deleteTarget.username}
            onDone={() => {
              setDeleteTarget(null);
              qc.invalidateQueries({ queryKey: ['admin', 'users'] });
            }}
          />
        )}
      </Modal>

      <Modal
        open={freezeTarget !== null}
        title={`冻结用户 — ${freezeTarget?.username ?? ''}`}
        onCancel={() => {
          setFreezeTarget(null);
          freezeForm.resetFields();
        }}
        onOk={() => freezeForm.submit()}
        destroyOnClose
      >
        <Form form={freezeForm} layout="vertical" onFinish={submitFreeze}>
          <Form.Item name="reason" label="冻结原因" rules={[{ required: true, min: 3 }]}>
            <Input.TextArea rows={3} placeholder="必填，将写入用户记录" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
